// src/manager/pages/VolatilityControl.tsx — Manager Per-Table Volatility
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, Zap, TrendingUp, TrendingDown } from 'lucide-react';
import { fetchCsrfToken } from '../../shared/api';

interface VolatilityProfile { tier: string; label: string; description: string; hpMult: number; spawnRate: number; bossWeight: number; }
interface TableRow { id: string; name: string; current_rtp: number; volatility_tier?: string; }

const TIER_COLORS: Record<string, string> = {
  LOW: 'bg-green-100 text-green-800 border-green-300',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  HIGH: 'bg-red-100 text-red-800 border-red-300',
};
const TIER_ICONS: Record<string, React.ReactNode> = {
  LOW: <TrendingDown size={14} className="inline mr-1" />,
  MEDIUM: <Zap size={14} className="inline mr-1" />,
  HIGH: <TrendingUp size={14} className="inline mr-1" />,
};

export default function VolatilityControl() {
  const token = localStorage.getItem('managerToken');
  const [tables, setTables] = useState<TableRow[]>([]);
  const [profiles, setProfiles] = useState<VolatilityProfile[]>([]);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [tabRes, profRes] = await Promise.all([
        fetch('/manager/tables', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
        fetch('/manager/volatility-profiles', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
      ]);
      const tabs = await tabRes.json(); const profs = await profRes.json();
      setTables(Array.isArray(tabs) ? tabs : []);
      setProfiles(Array.isArray(profs) ? profs : []);
      const init: Record<string, string> = {};
      (Array.isArray(tabs) ? tabs : []).forEach((t: TableRow) => { init[t.id] = t.volatility_tier || 'MEDIUM'; });
      setSelected(init);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (tableId: string) => {
    const tier = selected[tableId];
    setSaving(tableId);
    try {
      const csrfToken = await fetchCsrfToken().catch(() => '');
      const res = await fetch(`/manager/tables/${tableId}/volatility`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ tier }),
      });
      if (!res.ok) throw new Error((await res.json()).error?.message);
      toast.success(`Volatility → ${tier}`);
      setTables(prev => prev.map(t => t.id === tableId ? { ...t, volatility_tier: tier } : t));
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(null); }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Table Volatility</h2>
        <p className="text-gray-500 mt-1">Controls fish spawn patterns and win frequency for your tables</p>
      </div>

      {/* Profile Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {profiles.map(p => (
          <div key={p.tier} className={`border-2 rounded-xl p-4 ${TIER_COLORS[p.tier]}`}>
            <div className="flex items-center gap-2 mb-2 font-bold">{TIER_ICONS[p.tier]}{p.tier} — {p.label}</div>
            <p className="text-sm mb-2">{p.description}</p>
            <div className="text-xs font-mono space-y-0.5">
              <div>HP Multiplier: {p.hpMult}x</div>
              <div>Boss Weight: {p.bossWeight}x</div>
              <div>Spawn Rate: {(p.spawnRate * 100).toFixed(0)}%</div>
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-purple-600" size={36} /></div>
      ) : (
        <div className="bg-white shadow rounded-xl overflow-hidden border border-gray-200">
          <div className="p-4 border-b bg-gray-50 font-semibold text-gray-800">My Tables</div>
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-700 to-purple-500 text-white text-sm">
              <tr>
                <th className="p-3 text-left">Table</th>
                <th className="p-3 text-center">RTP</th>
                <th className="p-3 text-center">Current Volatility</th>
                <th className="p-3 text-center">Change To</th>
                <th className="p-3 text-center">Save</th>
              </tr>
            </thead>
            <tbody>
              {tables.map(t => (
                <tr key={t.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{t.name}</td>
                  <td className="p-3 text-center font-bold text-purple-700">
                    {(Number(t.current_rtp) * 100).toFixed(1)}%
                  </td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${TIER_COLORS[t.volatility_tier || 'MEDIUM']}`}>
                      {TIER_ICONS[t.volatility_tier || 'MEDIUM']}{t.volatility_tier || 'MEDIUM'}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <select value={selected[t.id] || 'MEDIUM'}
                      onChange={e => setSelected(p => ({ ...p, [t.id]: e.target.value }))}
                      className="border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500">
                      <option value="LOW">LOW</option>
                      <option value="MEDIUM">MEDIUM</option>
                      <option value="HIGH">HIGH</option>
                    </select>
                  </td>
                  <td className="p-3 text-center">
                    <button onClick={() => save(t.id)} disabled={saving === t.id || selected[t.id] === (t.volatility_tier || 'MEDIUM')}
                      className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
                      {saving === t.id ? <Loader2 className="animate-spin" size={14} /> : 'Apply'}
                    </button>
                  </td>
                </tr>
              ))}
              {tables.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-gray-400">No tables found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
